import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const SHIPPING_FLAT = 6.99;
const FREE_SHIPPING_THRESHOLD = 25.00;
export const TAX_RATE = 0.07;
const STORAGE_KEY = "wpf-cart-v1";

const CartContext = createContext(null);

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}

// Unique key so the same product with a different size is a separate cart line.
const lineKey = (item) => `${item.slug || item.name}::${item.variant || ""}`;

function loadInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadInitial);
  const [isOpen, setIsOpen] = useState(false);

  // Persist to localStorage whenever cart changes.
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* ignore quota */ }
  }, [items]);

  const addItem = useCallback((item, qty = 1) => {
    setItems((cur) => {
      const key = lineKey(item);
      const idx = cur.findIndex((c) => lineKey(c) === key);
      if (idx >= 0) {
        const next = [...cur];
        next[idx] = { ...next[idx], qty: next[idx].qty + qty };
        return next;
      }
      return [
        ...cur,
        {
          slug: item.slug || item.name,
          name: item.name,
          price: Number(item.price),
          image: item.image || null,
          variant: item.variant || "",  // e.g. size for tees
          qty,
        },
      ];
    });
    setIsOpen(true);  // pop cart open on add for immediate feedback
  }, []);

  const updateQty = useCallback((key, qty) => {
    setItems((cur) => cur.map((c) => (lineKey(c) === key ? { ...c, qty: Math.max(1, qty) } : c)));
  }, []);

  const removeItem = useCallback((key) => {
    setItems((cur) => cur.filter((c) => lineKey(c) !== key));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const totals = useMemo(() => {
    const itemsTotal = items.reduce((sum, it) => sum + it.price * it.qty, 0);
    // Free shipping when the cart's item subtotal is >= $25.
    const freeShipping = itemsTotal >= FREE_SHIPPING_THRESHOLD;
    const shipping = items.length > 0 && !freeShipping ? SHIPPING_FLAT : 0;
    const tax = Math.round(itemsTotal * TAX_RATE * 100) / 100;
    const grandTotal = itemsTotal + shipping + tax;
    const count = items.reduce((n, it) => n + it.qty, 0);
    // Keep every currency string to 2dp — required by PayPal breakdown validation.
    const fmt = (n) => n.toFixed(2);
    return {
      count,
      itemsTotal, itemsTotalStr: fmt(itemsTotal),
      shipping, shippingStr: fmt(shipping),
      tax, taxStr: fmt(tax),
      grandTotal, grandTotalStr: fmt(grandTotal),
      freeShipping,
      freeShippingThreshold: FREE_SHIPPING_THRESHOLD,
      amountToFreeShipping: Math.max(0, FREE_SHIPPING_THRESHOLD - itemsTotal),
    };
  }, [items]);

  const value = useMemo(
    () => ({
      items, isOpen,
      openCart: () => setIsOpen(true),
      closeCart: () => setIsOpen(false),
      toggleCart: () => setIsOpen((o) => !o),
      addItem, updateQty, removeItem, clear,
      lineKey,
      totals,
      SHIPPING_FLAT,
    }),
    [items, isOpen, addItem, updateQty, removeItem, clear, totals],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
